import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useAlerts } from '../../context/AlertContext';
import {
  LayoutDashboard,
  Package,
  FolderTree,
  MapPin,
  Wrench,
  Truck,
  History,
  Users,
  Bell,
  LogOut,
  Menu,
  X,
  FileText,
  ShoppingCart,
  Settings,
  ArrowLeftRight,
  TrendingUp,
  Users2,
  PackageMinus
} from 'lucide-react';
import { useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const Sidebar = () => {
  const { user, logout, isAdmin } = useAuth();
  const { pendingAlerts } = useAlerts();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/parts', icon: Package, label: 'Parts' },
    { to: '/categories', icon: FolderTree, label: 'Categories' },
    { to: '/locations', icon: MapPin, label: 'Locations' },
    { to: '/tools', icon: Wrench, label: 'Tools' },
    { to: '/suppliers', icon: Truck, label: 'Suppliers' },
    { to: '/customers', icon: Users2, label: 'Customers' },
    { to: '/stock-issuance', icon: PackageMinus, label: 'Stock Issuance' },
    { to: '/transfers', icon: ArrowLeftRight, label: 'Transfers' },
    { to: '/purchase-orders', icon: ShoppingCart, label: 'Purchase Orders' },
    { to: '/reorder-suggestions', icon: TrendingUp, label: 'Reorder Suggestions' },
    { to: '/reports', icon: FileText, label: 'Reports' },
    { to: '/settings', icon: Settings, label: 'Settings' },
  ];

  // Admin-only menu items
  if (isAdmin()) {
    navItems.push({ to: '/audit-log', icon: History, label: 'Audit Log' });
    navItems.push({ to: '/users', icon: Users, label: 'Users' });
  }

  const NavContent = () => (
    <>
      <div className="p-4 md:p-6 border-b border-slate-700 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-red-600 rounded-sm flex items-center justify-center">
            <span className="text-white font-black text-lg">911</span>
          </div>
          <div>
            <h1 className="text-white font-bold text-sm tracking-tight">PRO HOME LIVE</h1>
            <p className="text-slate-400 text-xs">INVENTORY</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 p-3 md:p-4 space-y-1 overflow-y-auto">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 md:py-2.5 rounded-sm text-sm font-medium transition-all duration-200 ${
                isActive
                  ? 'bg-slate-700 text-white'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              }`
            }
          >
            <item.icon size={18} strokeWidth={1.5} />
            {item.label}
          </NavLink>
        ))}
        
        <NavLink
          to="/alerts"
          onClick={() => setMobileOpen(false)}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2 md:py-2.5 rounded-sm text-sm font-medium transition-all duration-200 ${
              isActive
                ? 'bg-slate-700 text-white'
                : 'text-slate-300 hover:bg-slate-800 hover:text-white'
            }`
          }
        >
          <Bell size={18} strokeWidth={1.5} />
          Alerts
          {pendingAlerts.length > 0 && (
            <Badge variant="destructive" className="ml-auto rounded-sm text-xs px-1.5 py-0.5">
              {pendingAlerts.length}
            </Badge>
          )}
        </NavLink>
      </nav>

      <div className="p-3 md:p-4 border-t border-slate-700 flex-shrink-0">
        <div className="flex items-center gap-3 px-3 py-2 mb-2">
          <div className="w-8 h-8 bg-slate-600 rounded-sm flex items-center justify-center">
            <span className="text-white text-xs font-medium">
              {user?.name?.charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-medium truncate">{user?.name}</p>
            <p className="text-slate-400 text-xs capitalize">{user?.role}</p>
          </div>
        </div>
        <Button
          variant="ghost"
          className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-800 rounded-sm"
          onClick={handleLogout}
          data-testid="logout-btn"
        >
          <LogOut size={18} strokeWidth={1.5} className="mr-3" />
          Logout
        </Button>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile menu button */}
      <button
        className="fixed top-4 left-4 z-50 md:hidden p-2 bg-slate-900 text-white rounded-sm"
        onClick={() => setMobileOpen(!mobileOpen)}
        data-testid="mobile-menu-btn"
      >
        {mobileOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar - always fixed positioned, hidden off-screen on mobile */}
      <aside
        className={`sidebar-texture fixed left-0 top-0 h-screen w-64 bg-slate-900 flex flex-col z-40 transition-transform duration-300 md:translate-x-0 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <NavContent />
      </aside>
    </>
  );
};

export default Sidebar;
